import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowRight}=FiIcons;

const HeroSlideshow=({badge,title,subtitle,primaryCtaLabel,secondaryCtaLabel,onPrimaryCtaClick,onSecondaryCtaClick,features=[],})=> {
  const [currentSlide,setCurrentSlide]=useState(0);

  // Images de fond - contexte professionnel et africain (5 images)
  const backgroundImages=[
    'https://images.unsplash.com/photo-1483004406427-6acb078d1f2d?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Équipe professionnelle africaine
    'https://images.unsplash.com/photo-1505413687799-90481dfc0203?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Environnement de travail collaboratif
    'https://images.unsplash.com/photo-1542349314-b0ceb4d90f2d?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Réunion d'équipe
    'https://images.unsplash.com/photo-1647364191435-565bf3375048?q=80&w=1331&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Workspace moderne africain
    'https://images.unsplash.com/photo-1699412846091-eb870e284479?q=80&w=1174&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Environnement professionnel
  ];

  // Slideshow automatique - change toutes les 3 secondes
  useEffect(()=> {
    const interval=setInterval(()=> {
      setCurrentSlide((prev)=> (prev + 1) % backgroundImages.length);
    },3000);
    return ()=> clearInterval(interval);
  },[backgroundImages.length]);

  const containerVariants={
    hidden: {opacity: 0},
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants={
    hidden: {opacity: 0,y: 30},
    visible: {opacity: 1,y: 0},
  };

  const buttonVariants={
    hidden: {opacity: 0,scale: 0.9},
    visible: {opacity: 1,scale: 1},
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-900">
      {/* Slideshow Background Container */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Background images with smooth transitions - 2 secondes de transition */}
        {backgroundImages.map((image,index)=> (
          <motion.div
            key={index}
            initial={{opacity: 0}}
            animate={{opacity: currentSlide===index ? 1 : 0}}
            transition={{duration: 2,ease: 'easeInOut'}}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ))}

        {/* Gradient overlay - plus sombre pour faire ressortir le texte coloré */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-blue-900/80 to-gray-900/90 z-10" />

        {/* Texture overlay */}
        <div className="absolute inset-0 opacity-[0.05] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] z-10 pointer-events-none"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl relative z-20">
        <motion.div
          className="text-center space-y-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          transition={{duration: 0.8,ease: [0.25,0.1,0.25,1]}}
        >
          {badge && (
            <motion.div className="flex justify-center" variants={itemVariants}>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/20 text-blue-200 border border-blue-400/30 backdrop-blur-md hover:bg-blue-500/30 transition-colors duration-300 text-sm font-bold tracking-wide uppercase shadow-lg">
                {badge}
              </span>
            </motion.div>
          )}

          <motion.div className="space-y-6" variants={itemVariants}>
            <motion.h1
              role="heading"
              aria-level={1}
              className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-tight tracking-tight drop-shadow-2xl"
              variants={itemVariants}
            >
              {title}
            </motion.h1>

            <motion.div
              className="max-w-4xl mx-auto text-xl sm:text-2xl text-gray-200 leading-relaxed font-light drop-shadow-md"
              variants={itemVariants}
            >
              {subtitle}
            </motion.div>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
            variants={buttonVariants}
          >
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={onPrimaryCtaClick}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all duration-300 shadow-[0_0_25px_rgba(6,182,212,0.4)] text-lg border border-white/10"
            >
              {primaryCtaLabel}
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </motion.button>

            {secondaryCtaLabel && onSecondaryCtaClick && (
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={onSecondaryCtaClick}
                className="inline-flex items-center px-8 py-4 border-2 border-white/20 text-white font-semibold rounded-lg hover:bg-white/10 hover:border-white/40 transition-all duration-300 backdrop-blur-sm text-lg"
              >
                {secondaryCtaLabel}
              </motion.button>
            )}
          </motion.div>

          {features.length > 0 && (
            <motion.div className="pt-16 hidden md:block" variants={itemVariants}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {features.map((feature,index)=> (
                  <motion.div
                    key={index}
                    className="flex items-center justify-center space-x-3 bg-white/5 backdrop-blur-sm py-3 px-6 rounded-full border border-white/10 hover:bg-white/10 transition-colors"
                    initial={{opacity: 0,y: 20}}
                    animate={{opacity: 1,y: 0}}
                    transition={{duration: 0.6,delay: 0.8 + index * 0.1}}
                  >
                    <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]"></div>
                    <p className="text-white/90 font-medium tracking-wide">{feature}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Slide indicators - maintenant avec 5 indicateurs */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-30">
        {backgroundImages.map((_,index)=> (
          <motion.button
            key={index}
            onClick={()=> setCurrentSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              currentSlide===index
                ? 'bg-cyan-400 w-12 shadow-[0_0_10px_rgba(34,211,238,0.8)]'
                : 'bg-white/30 w-3 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlideshow;